import { useState, useCallback, useEffect, useRef } from 'react';
import storageService from '../storage/storageService';
import { queueSync } from '../storage/supabaseSync';
import { supabase } from '../../auth/supabaseClient';

/**
 * React hook wrapping storageService for reactive state management.
 * Now with Supabase sync: writes to localStorage immediately (fast) and
 * queues a debounced sync to Supabase when authenticated.
 *
 * Usage:
 *   const [cards, setCards] = useLocalStorage('flashcards', 'all', []);
 */
export default function useLocalStorage(namespace, key, defaultValue) {
  const [storedValue, setStoredValue] = useState(() => {
    return storageService.get(namespace, key, defaultValue);
  });
  const userIdRef = useRef(null);

  // Track current user ID for sync
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      userIdRef.current = session?.user?.id || null;
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      userIdRef.current = session?.user?.id || null;
    });
    return () => subscription.unsubscribe();
  }, []);

  const setValue = useCallback((value) => {
    const newValue = value instanceof Function ? value(storedValue) : value;
    setStoredValue(newValue);
    storageService.set(namespace, key, newValue);

    // Queue Supabase sync if authenticated
    if (userIdRef.current) {
      queueSync(namespace, key, newValue, userIdRef.current);
    }
  }, [namespace, key, storedValue]);

  return [storedValue, setValue];
}
